import { type ClassValue, clsx } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { unified } from 'unified'
import remarkParse from 'remark-parse'
import remarkStringify from 'remark-stringify'
import remarkRehype from 'remark-rehype'
import rehypeStringify from 'rehype-stringify'
import { visit } from 'unist-util-visit'
import type { Text } from 'mdast'

/**
 * `.text-meta` (src/index.css) is a component class that means exactly
 * `text-xs text-muted-foreground` — so tailwind-merge has to treat it as a FONT
 * SIZE. Left unregistered it falls into the catch-all `text-{colour}` group,
 * and both halves of the idiom break silently:
 *
 *  - `text-sm … text-meta` keeps the `text-sm`, and because `.text-meta` lives in
 *    `@layer components` (emitted before utilities) the utility wins the cascade —
 *    the element renders 14px, not 12px;
 *  - `text-meta text-foreground` drops `text-meta` altogether as a "superseded
 *    colour", costing the element its size as well as its colour.
 *
 * Registering it here restores the substitution the raw `text-xs` had: it
 * displaces an earlier size, yields to a later one, and never fights a colour.
 * Pinned by the `cn + .text-meta` cases in utils.test.ts.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': ['text-meta'],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Append an optimistically-created entity to a list, unless a server read has
 * already put it there.
 *
 * Upload flows race their own list refresh. `POST /documents` inserts the row —
 * `processing_status` takes its column default `'pending'` — then fires the
 * processor Lambda and returns the *insert-time* snapshot. The row exists
 * server-side the moment the INSERT commits, so a poll tick or a filter-driven
 * refetch issued while the create call is still in flight comes back with that
 * document already flipped to `'processing'`. If that response lands first, a
 * blind `[...documents, created]` shows the file twice: one row `processing`
 * (server truth) and one row `pending` (the stale create snapshot), both under
 * the same id — which also collides React's list keys.
 *
 * The entry already in the list wins outright. It can only have come from a
 * server read, so it is never staler than the create response, which is a
 * snapshot of the row as it was before any processing began.
 */
export function appendIfAbsentById<T extends { id: string }>(list: T[], incoming: T): T[] {
  return list.some((item) => item.id === incoming.id) ? list : [...list, incoming]
}

/**
 * Strip citation markers [N] from text while preserving code blocks.
 *
 * This function removes citation patterns like [1], [2], [10] from markdown text,
 * but preserves them inside fenced code blocks (```) and inline code (`).
 * This prevents accidentally removing valid syntax like array[1], JSON indices, etc.
 *
 * @param text - The markdown text containing citations
 * @returns The text with citations removed, but code blocks preserved
 *
 * @example
 * stripCitations("Check this [1] and [2]")
 * // => "Check this  and "
 *
 * @example
 * stripCitations("See [1]\n```js\narray[1]\n```")
 * // => "See \n```js\narray[1]\n```"
 */
export function stripCitations(text: string): string {
  const CITATION_REGEX = /\[(\d+)\]/g
  const FENCED_CODE_REGEX = /```[\s\S]*?```/g
  const INLINE_CODE_REGEX = /`[^`]+`/g

  // Find all code blocks (both fenced and inline)
  const codeBlocks: Array<{ start: number; end: number; content: string }> = []

  // Extract fenced code blocks
  let match: RegExpExecArray | null
  while ((match = FENCED_CODE_REGEX.exec(text)) !== null) {
    codeBlocks.push({
      start: match.index,
      end: match.index + match[0].length,
      content: match[0],
    })
  }

  // Extract inline code
  INLINE_CODE_REGEX.lastIndex = 0
  while ((match = INLINE_CODE_REGEX.exec(text)) !== null) {
    codeBlocks.push({
      start: match.index,
      end: match.index + match[0].length,
      content: match[0],
    })
  }

  // Sort code blocks by start position
  codeBlocks.sort((a, b) => a.start - b.start)

  // Build result by processing segments between code blocks
  let result = ''
  let lastIndex = 0

  for (const block of codeBlocks) {
    // Process text before this code block (strip citations)
    const beforeBlock = text.slice(lastIndex, block.start)
    result += beforeBlock.replace(CITATION_REGEX, '')

    // Add the code block unchanged
    result += block.content

    lastIndex = block.end
  }

  // Process remaining text after last code block
  const afterBlocks = text.slice(lastIndex)
  result += afterBlocks.replace(CITATION_REGEX, '')

  return result
}

/**
 * Strip citation markers [N] from markdown text while preserving code blocks.
 * Uses AST-based parsing to correctly identify and skip code/inlineCode nodes.
 *
 * @param markdown - The markdown text containing citations
 * @returns The markdown with citations removed, but code blocks intact
 *
 * @example
 * stripCitationsOutsideCode("Check `array[1]` and ref [2]")
 * // => "Check `array[1]` and ref "
 */
export function stripCitationsOutsideCode(markdown: string): string {
  const processor = unified()
    .use(remarkParse)
    .use(() => (tree) => {
      visit(tree, 'text', (node: Text) => {
        // Only strip citations in text nodes (not code/inlineCode)
        // Also remove optional space before citation: " [N]" or "[N]"
        node.value = node.value.replace(/ ?\[(\d+)\]/g, '')
      })
    })
    .use(remarkStringify)

  return String(processor.processSync(markdown))
}

/**
 * Convert markdown to plain text suitable for copy/paste.
 * Strips citations, removes markdown formatting, and cleans up spacing.
 *
 * @param markdown - The markdown text to convert
 * @returns Plain text with no markdown formatting
 *
 * @example
 * markdownToPlaintext("# Title [1]\n\n**Bold** text [2].")
 * // => "Title\n\nBold text."
 */
export function markdownToPlaintext(markdown: string): string {
  // First strip citations safely (outside code only)
  const withoutCitations = stripCitationsOutsideCode(markdown)

  const processor = unified().use(remarkParse)
  const tree = processor.parse(withoutCitations)

  let result = ''

  // Walk AST and extract text content
  visit(tree, (node) => {
    if (node.type === 'text') {
      result += node.value
    } else if (node.type === 'code') {
      result += node.value + '\n'
    } else if (node.type === 'inlineCode') {
      result += node.value
    } else if (node.type === 'break') {
      result += '\n'
    } else if (node.type === 'paragraph') {
      // Add newline after paragraphs for spacing
      result += '\n'
    }
  })

  // Post-processing cleanup
  result = result.replace(/\s+([.,;:!?])/g, '$1') // Space before punctuation
  result = result.replace(/[ \t]+/g, ' ')          // Multiple spaces
  result = result.replace(/[ \t]+\n/g, '\n')       // Trailing spaces
  result = result.replace(/\n{3,}/g, '\n\n')       // Excessive line breaks

  return result.trim()
}

/**
 * Convert markdown to HTML suitable for rich paste into Word/Google Docs.
 * Strips citations but preserves formatting (bold, headers, lists, etc.).
 *
 * @param markdown - The markdown text to convert
 * @returns HTML string with formatting preserved
 *
 * @example
 * markdownToHtml("# Title [1]\n\n**Bold** text [2].")
 * // => "<h1>Title</h1>\n<p><strong>Bold</strong> text.</p>"
 */
export function markdownToHtml(markdown: string): string {
  // First strip citations safely
  const withoutCitations = stripCitationsOutsideCode(markdown)

  const processor = unified()
    .use(remarkParse)
    .use(remarkRehype)
    .use(rehypeStringify)

  return String(processor.processSync(withoutCitations))
}

// ===================
// Suggestion diff markup
// ===================

/** Cheap probe so the common (clean) case never pays for a DOM parse. */
const DIFF_MARKUP_PROBE = /<ins\b|<del\b|data-diff=|diff-(?:insert|delete)/i

/** Insertions: the AI's proposed new text. Deletions: the text it wants gone. */
const INSERTION_SELECTOR = 'ins, [data-diff="insert"], .diff-insert'
const DELETION_SELECTOR = 'del, [data-diff="delete"], .diff-delete'

/**
 * Collapse AI-suggestion diff decorations out of draft HTML.
 *
 * While a draft-patch preview is live the section editor's document is the
 * *diff*, not the draft: the insertion/deletion marks (`diffMarks.ts`) serialize
 * to `<ins class="diff-insert">` / `<del class="diff-delete">`. That document must
 * never reach the server — E2E F1 saw a save race the preview and persist it,
 * leaving literal diff spans and duplicated words ("formalthe following") in the
 * stored draft, surviving reload.
 *
 * Insertions are dropped and deletions unwrapped, i.e. the diff collapses back to
 * the text the *user* had. That direction is the safe one: a stray save can never
 * silently accept an AI change the user never approved. Accepting a suggestion goes
 * through the preview's own accept path, which persists the server-applied content.
 */
export function stripDiffMarkup(html: string): string {
  if (!html || !DIFF_MARKUP_PROBE.test(html)) return html

  if (typeof DOMParser === 'undefined') {
    // Non-DOM environments (SSR/node): marks are inline and never nest, so a
    // non-greedy pass is exact for the shapes diffMarks emits and parses.
    return html
      .replace(/<ins\b[^>]*>[\s\S]*?<\/ins>/gi, '')
      .replace(/<span\b[^>]*data-diff="insert"[^>]*>[\s\S]*?<\/span>/gi, '')
      .replace(/<\/?del\b[^>]*>/gi, '')
      .replace(/<s\b[^>]*data-diff="delete"[^>]*>([\s\S]*?)<\/s>/gi, '$1')
  }

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  doc.body.querySelectorAll(INSERTION_SELECTOR).forEach((el) => el.remove())
  doc.body.querySelectorAll(DELETION_SELECTOR).forEach((el) => {
    el.replaceWith(...Array.from(el.childNodes))
  })
  return doc.body.innerHTML
}
